import { Outlet, useLocation, useNavigate } from "react-router";
import {
  BarChart3,
  ExternalLink,
  FileText,
  HelpCircle,
  Layers,
  Link2,
  Palette,
  PanelBottom,
  Plus,
} from "lucide-react";
import { Button, PageHeader, type HeaderTab } from "../../components/ui";
import { useActiveTenant } from "../../auth/AuthProvider";
import { publicSiteBase } from "../../hooks/publicSiteUrl";

const ICON = { size: 14, strokeWidth: 1.8 } as const;

const TABS: HeaderTab[] = [
  {
    key: "/website/analytics",
    to: "/website/analytics",
    label: "Analytics",
    icon: <BarChart3 {...ICON} />,
    description:
      "See how visitors find and browse your website -- page views, traffic sources, and device breakdown. Updated daily.",
  },
  {
    key: "/website/pages",
    to: "/website/pages",
    label: "Pages",
    icon: <Layers {...ICON} />,
    description:
      "The main pages of your website — home, about, services, and more. Build each page from modular content rows and publish when ready.",
  },
  {
    key: "/website/blog",
    to: "/website/blog",
    label: "Blog",
    icon: <FileText {...ICON} />,
    description:
      "Articles published on your website. Write, schedule, and tag posts — regular content helps new clients find you in search results and shows your expertise before they ever book.",
  },
  {
    key: "/website/faqs",
    to: "/website/faqs",
    label: "FAQs",
    icon: <HelpCircle {...ICON} />,
    description:
      "Common questions with your answers, shown on the landing pages you choose. Clearing up doubts ahead of time makes visitors more comfortable booking with you.",
  },
  {
    key: "/website/custom-urls",
    to: "/website/custom-urls",
    label: "Custom URLs",
    icon: <Link2 {...ICON} />,
    description:
      "Short, memorable links on your own domain that send visitors to a landing page or anywhere on your site. Great for business cards, ads, and QR codes — each one tracks clicks and bookings so you know what's working.",
  },
  {
    key: "/website/chrome",
    to: "/website/chrome",
    label: "Header & Footer",
    icon: <PanelBottom {...ICON} />,
    description:
      "Your site header navigation and footer in one visual editor. Drag to reorder links, pick a footer layout, and configure the announcement bar.",
  },
  {
    key: "/website/branding",
    to: "/website/branding",
    label: "Branding",
    icon: <Palette {...ICON} />,
    description:
      "Your logo and colors, applied across your public website so every page looks unmistakably like your practice.",
  },
];

/** The "new X" action depends on which view is open. */
const PRIMARY_ACTION: Record<string, { label: string; to: string }> = {
  "/website/pages": { label: "New page", to: "/website/pages/new" },
  "/website/blog": { label: "New post", to: "/website/blog/new" },
  "/website/custom-urls": { label: "New URL", to: "/website/custom-urls/new" },
  "/website/faqs": { label: "New FAQ", to: "/website/faqs/new" },
};

/**
 * Chrome for the Website section. The breadcrumb and page title live in the
 * app TopBar, so this renders only the view tabs and the section's actions —
 * the same header every other page in the portal uses.
 *
 * Editor screens (`/website/blog/:id`, `/website/pages/:id`) deliberately
 * bypass this shell and render their own full-screen chrome.
 */
export function WebsiteShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tenant = useActiveTenant();

  const activeTab =
    TABS.map((t) => t.to!)
      .filter((to) => pathname === to || pathname.startsWith(`${to}/`))
      .sort((a, b) => b.length - a.length)[0] ?? "";
  const primaryAction = PRIMARY_ACTION[activeTab] ?? null;

  return (
    <div className="min-h-full">
      <PageHeader
        tabsLabel="Website sections"
        tabs={TABS}
        activeTab={activeTab}
        actions={
          <>
            <a
              href={publicSiteBase(tenant.domain)}
              target="_blank"
              rel="noopener noreferrer"
              className="hair flex items-center gap-1.5 rounded-md border bg-paper-hi px-3.5 py-2 text-[13px] text-ink-2 hover:bg-paper-2"
            >
              <ExternalLink size={13} strokeWidth={1.8} />
              View site
            </a>
            {primaryAction && (
              <Button
                variant="primary"
                icon={<Plus size={13} strokeWidth={2.2} />}
                onClick={() => navigate(primaryAction.to)}
              >
                {primaryAction.label}
              </Button>
            )}
          </>
        }
        mobileAction={
          primaryAction
            ? {
                label: primaryAction.label,
                icon: <Plus size={16} strokeWidth={2.2} />,
                onClick: () => navigate(primaryAction.to),
              }
            : undefined
        }
      />

      <div className="px-4 md:px-7 pb-20">
        <Outlet />
      </div>
    </div>
  );
}
